import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import { Tile, TileContent, TileControlGroup } from '~/ui/core/tile/Tile';

export const GrowthTileFrame: FunctionComponent<{
    readonly actions?: ReactNode;
    readonly children: ReactNode;
    readonly onClose: () => void;
}> = ({ actions, children, onClose }) => {
    const translate = useTranslate();

    return (
        <Tile className="relative">
            <div className="flex w-full shrink-0 items-center justify-between gap-m px-6xl pt-tile-edge">
                <TileControlGroup>
                    <Button
                        aria-label={translate('common.close')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={onClose}
                    >
                        <IconCrossSmall />
                    </Button>
                </TileControlGroup>
                <div className="flex min-w-0 flex-wrap items-center justify-end gap-s">{actions}</div>
            </div>
            <ScrollArea
                className="min-h-0 w-full flex-1"
                scrollFade="vertical"
                scrollFadeSize="var(--spacing-4xl)"
                type="scroll"
            >
                <TileContent className="h-auto! gap-section! pt-m pb-6xl!">{children}</TileContent>
            </ScrollArea>
        </Tile>
    );
};

export const GrowthTileSkeleton: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => (
    <GrowthTileFrame onClose={onClose}>
        <div className="flex w-full flex-col gap-stack">
            <Skeleton height="28px" width="80%" />
            <Skeleton height="16px" width="40%" />
        </div>
        <Skeleton height="96px" width="100%" />
        <Skeleton height="160px" width="100%" />
    </GrowthTileFrame>
);

export const DetailSection: FunctionComponent<{ readonly children: ReactNode; readonly title: string }> = ({
    children,
    title,
}) => (
    <section className="flex w-full flex-col gap-stack">
        <h3 className="m-0 text-body-s font-medium text-text-heading">{title}</h3>
        {children}
    </section>
);
